import { useRef, useState } from "react";
import Icon from "../../../../Icon";
import uploadIcon from "../../../../../assets/icons/cloud_upload.svg";
import {
  DragDropAreaContainer,
  DragDropLabel,
  DummyButton,
  DragDropText,
  FileInfo,
} from "./styles";

interface DragDropAreaProps {
  accept: string;
  onFileSelect: (file: File) => void;
  onError?: (error: string) => void;
  placeholder?: string;
  helperText?: string | React.ReactNode;
  maxSize?: number; // in MB
  icon?: string;
  buttonText?: string;
  className?: string;
}

const DragDropArea = ({
  accept,
  onFileSelect,
  onError,
  placeholder = "Arrastrá un archivo aquí",
  helperText,
  maxSize,
  icon,
  buttonText = "Seleccionar archivo",
  className = "",
}: DragDropAreaProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): boolean => {
    if (maxSize && file.size > maxSize * 1024 * 1024) {
      const errorMsg = `El archivo excede el tamaño máximo de ${maxSize}MB`;
      setError(errorMsg);
      onError?.(errorMsg);
      return false;
    }

    if (accept) {
      const fileName = file.name.toLowerCase();
      const acceptedExtensions = accept.split(',').map(type => {
        const trimmedType = type.trim();

        if (trimmedType.includes('/')) {
          // Handle wildcard types like "audio/*", "image/*"
          if (trimmedType.endsWith('/*')) {
            const baseType = trimmedType.split('/')[0];
            const wildcardExtensions: { [key: string]: string[] } = {
              'audio': ['.mp3', '.ogg', '.wav', '.aac', '.flac', '.m4a'],
              'image': ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp', '.svg'],
              'video': ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.flv'],
              'application': ['.pdf', '.doc', '.docx', '.txt', '.zip', '.rar']
            };
            return wildcardExtensions[baseType] || [];
          }

          // Convert specific MIME types to extensions
          const mimeToExt: { [key: string]: string[] } = {
            'video/mp4': ['.mp4'],
            'video/webm': ['.webm'],
            'video/quicktime': ['.mov', '.qt'],
            'video/mov': ['.mov'],
            'video/avi': ['.avi'],
            'video/mkv': ['.mkv'],
            'video/x-msvideo': ['.avi'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/webp': ['.webp'],
            'image/gif': ['.gif'],
            'image/bmp': ['.bmp'],
            'image/svg+xml': ['.svg'],
            'audio/mpeg': ['.mp3'],
            'audio/ogg': ['.ogg'],
            'audio/wav': ['.wav'],
            'audio/aac': ['.aac'],
            'audio/flac': ['.flac'],
            'audio/mp4': ['.m4a'],
            'audio/x-ms-wma': ['.wma'],
            'application/pdf': ['.pdf'],
            'application/x-pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'text/plain': ['.txt'],
            'application/zip': ['.zip'],
            'application/x-rar-compressed': ['.rar']
          };
          return mimeToExt[trimmedType] || [];
        } else {
          // Direct extension (ensure it starts with dot)
          return trimmedType.startsWith('.') ? [trimmedType] : [`.${trimmedType}`];
        }
      }).flat();

      // Check both extension and MIME type
      const isValidExtension = acceptedExtensions.some(ext =>
        fileName.endsWith(ext.toLowerCase())
      );

      // Check MIME type validation
      let isValidMimeType = false;

      if (file.type) {
        if (accept.includes('*')) {
          // Wildcard types like "audio/*", "image/*"
          const baseType = accept.split('*')[0].replace('/', '');
          isValidMimeType = file.type.startsWith(baseType);
        } else {
          // Specific MIME types like "application/pdf"
          const acceptedMimeTypes = accept.split(',').map(type => type.trim());
          isValidMimeType = acceptedMimeTypes.some(acceptedType =>
            file.type === acceptedType ||
            (acceptedType.includes('/') && file.type.startsWith(acceptedType.split('/')[0]))
          );
        }
      }


      if (!isValidExtension && !isValidMimeType) {
        const errorMsg = `Tipo de archivo no válido. Formatos aceptados: ${acceptedExtensions.join(', ')}`;
        setError(errorMsg);
        onError?.(errorMsg);
        return false;
      }
    }

    setError("");
    return true;
  };

  const handleFileSelect = (file: File) => {
    if (validateFile(file)) {
      setSelectedFile(file);
      onFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <DragDropAreaContainer
        className={`${selectedFile ? "has-file" : ""} ${className}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <DragDropLabel htmlFor="drag-drop-input">

          <Icon
            icon={uploadIcon}
            size="36px"
            color={"#bdbdbd"}
          />

          <div style={{ height: 16 }}></div>

          {selectedFile ? (
            <FileInfo>
              <p style={{ fontSize: "14px", margin: 0 }}>
                Archivo seleccionado
              </p>

              <p style={{ fontSize: "14px", margin: 0, lineHeight: "normal" }}><strong>{selectedFile.name}</strong></p>

              <div style={{ height: 10 }} />

              <DummyButton
                type="button"
                onClick={handleRemoveFile}
              >
                Cambiar archivo
              </DummyButton>

              <div style={{ height: 10 }} />
            </FileInfo>
          ) : (
            <>
              <DummyButton type="button" onClick={() => fileInputRef.current?.click()}>{buttonText}</DummyButton>
              {placeholder}
            </>
          )}

          {helperText && (
            <>
              <DragDropText>
                {helperText}
              </DragDropText>
            </>
          )}
        </DragDropLabel>

        <input
          ref={fileInputRef}
          type="file"
          id="drag-drop-input"
          accept={accept}
          style={{ display: "none" }}
          onChange={handleFileInputChange}
        />
      </DragDropAreaContainer>

      {error && (
        <div style={{ color: "#d32f2f", fontSize: "13px", marginTop: "8px" }}>
          {error}
        </div>
      )
      }
    </>
  );
};

export default DragDropArea;
